import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { StatusActionButton, BottomSheet, ButtonList } from '../../../shared/ui';
import { sitesV2Api, SiteV2 } from '../../../api/sitesV2Api';
import { compressImageFile } from '../../../utils/imageCompression';
import { openFileInput } from '../../../utils/filePicker';
import styles from './CreatePost.module.css';

interface CreatePostProps {
  onCreatePost: (content: string, images?: File[], siteId?: number | null) => Promise<void>;
  onCancel?: () => void;
  preselectedSiteId?: number | null;
}

const CreatePost: React.FC<CreatePostProps> = ({ onCreatePost, onCancel, preselectedSiteId }) => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [isExpanded, setIsExpanded] = useState(true); // Всегда развернуто
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [removingIndices, setRemovingIndices] = useState<Set<number>>(new Set());
  const [sites, setSites] = useState<SiteV2[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(preselectedSiteId || null);
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [siteSearchQuery, setSiteSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState<'compressing' | 'uploading'>('compressing');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const [readingProgress, setReadingProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const readingProgressRef = useRef<{ completed: number; failed: number[] }>({ completed: 0, failed: [] });
  const blobUrlsRef = useRef<Set<string>>(new Set());

  // Автофокус на textarea при монтировании
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Загрузка объектов при монтировании
  useEffect(() => {
    const loadSites = async () => {
      try {
        const mySites = await sitesV2Api.getMySites();
        setSites(mySites);
      } catch (error) {
        console.error('Error loading sites:', error);
      }
    };
    loadSites();
  }, []);

  // Синхронизация с preselectedSiteId
  useEffect(() => {
    if (preselectedSiteId !== undefined) {
      setSelectedSiteId(preselectedSiteId);
    }
  }, [preselectedSiteId]);

  // Очистка blob URL при размонтировании компонента
  useEffect(() => {
    return () => {
      // Освобождаем все blob URL при размонтировании
      blobUrlsRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  const handleFocus = () => {
    setIsExpanded(true);
  };

  // Вспомогательная функция для проверки, является ли файл изображением
  const isImageFile = (file: File): boolean => {
    // Проверяем MIME тип
    if (file.type && file.type.startsWith('image/')) {
      return true;
    }
    
    // На мобильных устройствах file.type может быть пустым, проверяем расширение
    const fileName = file.name.toLowerCase();
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'];
    return imageExtensions.some(ext => fileName.endsWith(ext));
  };

  const handleSubmit = async () => {
    if (!content.trim() && selectedImages.length === 0) {
      console.warn('Попытка отправить пустой пост');
      return;
    }
    
    if (isUploading) {
      console.warn('Загрузка уже идет, игнорируем повторную отправку');
      return; // Предотвращаем повторную отправку
    }

    console.log('=== НАЧАЛО ОТПРАВКИ ПОСТА ===');
    console.log('Состояние перед отправкой:', {
      contentLength: content.length,
      contentPreview: content.substring(0, 50),
      imagesCount: selectedImages.length,
      imagePreviewsCount: imagePreviews.length,
      selectedSiteId: selectedSiteId
    });

    // Проверяем, что количество превью совпадает с количеством файлов
    if (selectedImages.length !== imagePreviews.length) {
      console.warn(`Несоответствие: файлов ${selectedImages.length}, превью ${imagePreviews.length}`);
      // Это не критично, но стоит залогировать
    }

    // Валидация файлов перед отправкой (проверяем только тип и что файл не пустой)
    // Размер не ограничиваем, так как браузер сжимает изображения перед загрузкой
    const invalidFiles = selectedImages.filter(file => {
      const isValid = file.size > 0 && isImageFile(file);
      if (!isValid) {
        console.warn('Невалидный файл:', {
          name: file.name,
          size: file.size,
          type: file.type,
          isImage: isImageFile(file)
        });
      }
      return !isValid;
    });
    
    if (invalidFiles.length > 0) {
      const errorMsg = `Некоторые файлы не подходят. Проблемные файлы: ${invalidFiles.map(f => f.name).join(', ')}`;
      console.error('Валидация не прошла:', invalidFiles);
      setError(errorMsg);
      return;
    }

    // Вычисляем общий размер для лога (не для ограничения)
    const totalSize = selectedImages.reduce((sum, file) => sum + file.size, 0);
    
    console.log('Валидация пройдена, начинаем загрузку:', {
      imagesCount: selectedImages.length,
      totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`,
      imagesInfo: selectedImages.map(f => ({ 
        name: f.name, 
        size: `${(f.size / 1024 / 1024).toFixed(2)}MB`, 
        type: f.type || 'не определен' 
      }))
    });

    setError(null);
    setIsUploading(true);
    setUploadStage('compressing');
    setUploadProgress(0);

    let simulationInterval: ReturnType<typeof setInterval> | null = null;

    try {
      // Сжимаем изображения поочерёдно — чтобы показывать прогресс по фото
      let imagesToUpload = selectedImages;
      if (selectedImages.length > 0) {
        const opts = {
          maxWidth: 2560,
          maxHeight: 2560,
          quality: 0.85,
          maxSizeMB: 5,
          outputFormat: 'image/webp' as const,
        };
        const compressed: File[] = [];
        for (let i = 0; i < selectedImages.length; i++) {
          setUploadProgress(Math.round(((i) / selectedImages.length) * 100));
          try {
            compressed.push(await compressImageFile(selectedImages[i], opts));
          } catch {
            compressed.push(selectedImages[i]);
          }
        }
        setUploadProgress(100);
        imagesToUpload = compressed;
      }

      // Фаза загрузки: симулируем медленный рост до 90%, реальный ответ → 100%
      setUploadStage('uploading');
      setUploadProgress(0);
      let simProgress = 0;
      // ~1667ms per photo to reach 90% (45 steps × interval)
      const intervalMs = Math.max(50, Math.round(imagesToUpload.length * 37));
      simulationInterval = setInterval(() => {
        simProgress = Math.min(simProgress + 2, 90);
        setUploadProgress(simProgress);
      }, intervalMs);

      await onCreatePost(content, imagesToUpload.length > 0 ? imagesToUpload : undefined, selectedSiteId);

      clearInterval(simulationInterval);
      simulationInterval = null;
      // Быстро доезжаем до 100%
      simulationInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(simulationInterval!);
            simulationInterval = null;
            return 100;
          }
          return prev + 5;
        });
      }, 30);
      
      console.log('=== ПОСТ УСПЕШНО СОЗДАН ===');
      setContent('');
      setSelectedImages([]);
      setImagePreviews([]);
      setSelectedSiteId(preselectedSiteId || null);
      setIsExpanded(false);
    } catch (error: any) {
      console.error('=== ОШИБКА ПРИ СОЗДАНИИ ПОСТА ===');
      console.error('Детали ошибки:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        config: {
          url: error?.config?.url,
          method: error?.config?.method,
          data: error?.config?.data instanceof FormData ? 'FormData' : error?.config?.data
        }
      });
      
      // Извлекаем понятное сообщение об ошибке
      let errorMessage = 'Ошибка при публикации поста';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Дополнительная информация для отладки
      if (error?.response?.status === 413) {
        errorMessage = 'Файлы слишком большие. Попробуйте уменьшить размер изображений.';
      } else if (error?.response?.status === 400) {
        errorMessage = errorMessage || 'Неверный формат данных. Проверьте выбранные файлы.';
      } else if (error?.response?.status >= 500) {
        errorMessage = 'Ошибка сервера. Попробуйте позже.';
      } else if (!error?.response) {
        // Нет ответа от сервера (сеть, таймаут и т.д.)
        errorMessage = 'Не удалось подключиться к серверу. Проверьте интернет-соединение.';
      }
      
      setError(errorMessage);
      
      // Не очищаем форму при ошибке, чтобы пользователь мог попробовать снова
    } finally {
      if (simulationInterval !== null) clearInterval(simulationInterval);
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setContent('');
    setSelectedImages([]);
    setImagePreviews([]);
    setSelectedSiteId(preselectedSiteId || null);
    setIsExpanded(false);
    setError(null);
    onCancel?.();
  };

  // Получить название выбранного объекта
  const getSelectedSiteName = (): string => {
    if (!selectedSiteId) return 'Без объекта';
    const site = sites.find(s => s.id === selectedSiteId);
    return site?.name || 'Объект';
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    let files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    console.log('Выбрано файлов:', files.length);
    files.forEach((file, idx) => {
      console.log(`Файл ${idx + 1}:`, {
        name: file.name,
        size: file.size,
        type: file.type || 'не определен',
        lastModified: new Date(file.lastModified).toISOString()
      });
    });

    setError(null); // Очищаем предыдущие ошибки

    // Валидация файлов (проверяем только тип и что файл не пустой)
    // Размер не ограничиваем, так как браузер сжимает изображения перед загрузкой
    const invalidFiles = files.filter(file => {
      if (!isImageFile(file)) {
        return true;
      }
      if (file.size === 0) {
        return true; // Пустые файлы
      }
      return false;
    });

    if (invalidFiles.length > 0) {
      const notImages = files.filter(f => !isImageFile(f));
      const emptyFiles = files.filter(f => f.size === 0);
      
      let errorMsg = 'Некоторые файлы не подходят: ';
      if (notImages.length > 0) {
        errorMsg += `${notImages.length} файл(ов) не являются изображениями. `;
      }
      if (emptyFiles.length > 0) {
        errorMsg += `${emptyFiles.length} файл(ов) пустые.`;
      }
      setError(errorMsg);
      
      // Добавляем только валидные файлы
      const validFiles = files.filter(file => 
        isImageFile(file) && file.size > 0
      );
      
      if (validFiles.length === 0) {
        console.warn('Нет валидных файлов для добавления');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      console.log(`Валидных файлов: ${validFiles.length} из ${files.length}`);
      files = validFiles;
    }

    // Ограничиваем количество изображений (например, до 5)
    const maxImages = 10;
    const currentCount = selectedImages.length;
    const filesToAdd = files.slice(0, maxImages - currentCount);
    
    if (filesToAdd.length < files.length) {
      setError(`Можно добавить максимум ${maxImages} изображений. Добавлено ${filesToAdd.length} из ${files.length}.`);
    }
    
    if (filesToAdd.length === 0) {
      console.warn('Нельзя добавить больше файлов (достигнут лимит)');
      return;
    }

    console.log(`Добавляем ${filesToAdd.length} файлов`);
    
    // Сохраняем текущую длину для правильного индексирования
    const startIndex = selectedImages.length;
    
    // Сначала добавляем все файлы в selectedImages
    setSelectedImages(prev => [...prev, ...filesToAdd]);
    
    // Создаем плейсхолдеры для превью (показываем индикатор загрузки)
    setImagePreviews(prev => {
      const newPreviews = [...prev];
      // Добавляем плейсхолдеры для новых файлов
      for (let i = 0; i < filesToAdd.length; i++) {
        newPreviews.push(''); // Пустая строка будет означать "загрузка"
      }
      return newPreviews;
    });
    
    // Запускаем процесс чтения
    setIsReadingFiles(true);
    setReadingProgress({ current: 0, total: filesToAdd.length });
    
    // Сбрасываем ref для отслеживания прогресса
    readingProgressRef.current = { completed: 0, failed: [] };
    
    // Функция для проверки завершения чтения всех файлов
    const checkReadingComplete = (startIdx: number, totalFiles: number) => {
      const progress = readingProgressRef.current;
      if (progress.completed === totalFiles) {
        setIsReadingFiles(false);
        setReadingProgress({ current: 0, total: 0 });
        
        if (progress.failed.length > 0) {
          console.warn(`Не удалось прочитать ${progress.failed.length} из ${totalFiles} файлов`);
          if (progress.failed.length === totalFiles) {
            setError('Не удалось прочитать выбранные файлы. Возможно, они повреждены или недоступны.');
            // Удаляем файлы, если ни одно превью не удалось создать
            setSelectedImages(prev => prev.slice(0, startIdx));
            setImagePreviews(prev => prev.slice(0, startIdx));
            return;
          }
          
          // Удаляем файлы, для которых не удалось создать превью
          setSelectedImages(prev => {
            const newImages = [...prev];
            // Удаляем файлы в обратном порядке от конца массива
            for (let i = totalFiles - 1; i >= 0; i--) {
              if (progress.failed.includes(i)) {
                newImages.splice(startIdx + i, 1);
              }
            }
            return newImages;
          });
          
          // Также удаляем соответствующие превью
          setImagePreviews(prev => {
            const newPreviews = [...prev];
            // Удаляем превью в обратном порядке от конца массива
            for (let i = totalFiles - 1; i >= 0; i--) {
              if (progress.failed.includes(i)) {
                newPreviews.splice(startIdx + i, 1);
              }
            }
            return newPreviews;
          });
        }
      }
    };
    
    // Читаем каждый файл отдельно и обновляем превью сразу после чтения
    filesToAdd.forEach((file, relativeIndex) => {
      const absoluteIndex = startIndex + relativeIndex;
      
      console.log(`Читаем файл ${relativeIndex + 1}/${filesToAdd.length}:`, {
        name: file.name,
        size: file.size,
        type: file.type || 'не определен',
        absoluteIndex
      });
      
      // Проверяем, что файл доступен
      if (file.size === 0) {
        console.warn(`Файл ${file.name} пустой, пропускаем`);
        readingProgressRef.current.completed++;
        readingProgressRef.current.failed.push(relativeIndex);
        setReadingProgress(prev => ({ ...prev, current: prev.current + 1 }));
        checkReadingComplete(startIndex, filesToAdd.length);
        return;
      }
      
      // Используем createObjectURL вместо readAsDataURL для эффективности
      // Blob URL намного легче по памяти, чем base64 data URL
      try {
        const blobUrl = URL.createObjectURL(file);
        blobUrlsRef.current.add(blobUrl); // Отслеживаем blob URL для очистки
        console.log(`Файл ${file.name} успешно обработан, создан blob URL`);
        
        readingProgressRef.current.completed++;
        setReadingProgress(prev => ({ ...prev, current: prev.current + 1 }));
        
        // Сразу обновляем превью для этого файла
        setImagePreviews(prev => {
          const newPreviews = [...prev];
          if (absoluteIndex < newPreviews.length) {
            newPreviews[absoluteIndex] = blobUrl;
          }
          return newPreviews;
        });
        
        checkReadingComplete(startIndex, filesToAdd.length);
      } catch (error) {
        readingProgressRef.current.completed++;
        readingProgressRef.current.failed.push(relativeIndex);
        setReadingProgress(prev => ({ ...prev, current: prev.current + 1 }));
        console.error('Исключение при обработке файла:', file.name, error);
        checkReadingComplete(startIndex, filesToAdd.length);
      }
    });

    // Сбрасываем input для возможности повторного выбора
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = useCallback((index: number) => {
    // Мгновенное визуальное скрытие - элемент сразу исчезает из UI
    setRemovingIndices(prev => new Set(prev).add(index));
    
    // Получаем blob URL для освобождения памяти из текущего состояния
    setImagePreviews(prev => {
      const blobUrlToRevoke: string | undefined = prev[index];
      
      // Освобождаем память blob URL асинхронно
      if (blobUrlToRevoke && blobUrlToRevoke.startsWith('blob:')) {
        blobUrlsRef.current.delete(blobUrlToRevoke);
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            URL.revokeObjectURL(blobUrlToRevoke);
          }, { timeout: 100 });
        } else {
          setTimeout(() => {
            URL.revokeObjectURL(blobUrlToRevoke);
          }, 0);
        }
      }
      
      const newPreviews = [...prev];
      if (index < newPreviews.length) {
        newPreviews.splice(index, 1);
      }
      return newPreviews;
    });
    
    // Сразу удаляем файл из списка
    setSelectedImages(prev => {
      const newImages = [...prev];
      if (index < newImages.length) {
        newImages.splice(index, 1);
      }
      return newImages;
    });
    
    // Убираем из списка удаляемых
    setRemovingIndices(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const closeSiteSelector = () => {
    setShowSiteSelector(false);
    setSiteSearchQuery('');
  };

  const siteSelectorItems = useMemo(() => {
    const noSite = {
      id: 'no-site',
      icon: 'ri-close-line' as const,
      label: 'Без объекта',
      onClick: () => { setSelectedSiteId(null); closeSiteSelector(); }
    };
    const q = siteSearchQuery.trim().toLowerCase();
    if (!q) {
      return [
        noSite,
        ...sites.map(site => ({
          id: `site-${site.id}`,
          icon: 'ri-building-line' as const,
          label: site.name,
          onClick: () => { setSelectedSiteId(site.id); closeSiteSelector(); }
        }))
      ];
    }
    const filtered = sites.filter(s => s.name.toLowerCase().includes(q));
    if (filtered.length === 0) {
      return [
        noSite,
        { id: 'no-results', label: 'Объекты не найдены', onClick: () => {}, disabled: true }
      ];
    }
    return [
      noSite,
      ...filtered.map(site => ({
        id: `site-${site.id}`,
        icon: 'ri-building-line' as const,
        label: site.name,
        onClick: () => { setSelectedSiteId(site.id); closeSiteSelector(); }
      }))
    ];
  }, [sites, siteSearchQuery]);

  return (
    <div className={styles.createPost}>
      {isUploading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <i className="ri-loader-4-line ri-spin"></i>
            <span>
              {uploadStage === 'compressing'
                ? (selectedImages.length > 1
                    ? `Сжатие ${selectedImages.length} фото...`
                    : 'Сжатие фото...')
                : (selectedImages.length > 1
                    ? `Загрузка ${selectedImages.length} фото...`
                    : 'Загрузка фото...')}
            </span>
            {uploadStage === 'uploading' && (
              <>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressBarFill}
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className={styles.progressPercent}>{uploadProgress}%</span>
              </>
            )}
          </div>
        </div>
      )}
      
      <div className={styles.header}>
        <i className="ri-edit-line"></i>
        <span>Что нового?</span>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
          <button 
            className={styles.closeErrorButton}
            onClick={() => setError(null)}
            title="Закрыть"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      <div className={styles.inputContainer}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          placeholder="Поделитесь своими мыслями..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          rows={isExpanded ? 4 : 2}
          disabled={isUploading}
        />
      </div>

      <div className={styles.actions}>
        <div className={styles.tools}>
          <button 
            className={styles.toolButton}
            title="Добавить изображение"
            onClick={() => openFileInput(fileInputRef.current)}
            disabled={isUploading || isReadingFiles}
          >
            <i className="ri-image-line"></i>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
          <button 
            className={styles.toolButton}
            title="Добавить emoji (скоро)"
            disabled
          >
            <i className="ri-emotion-line"></i>
          </button>
          
          {/* Кнопка выбора объекта */}
          {sites.length > 0 && (
            <button 
              className={`${styles.toolButton} ${styles.siteButton} ${selectedSiteId ? styles.siteSelected : ''}`}
              title="Привязать к объекту"
              onClick={() => setShowSiteSelector(true)}
              disabled={isUploading}
            >
              <i className="ri-building-line"></i>
              {selectedSiteId && <span className={styles.siteDot}></span>}
            </button>
          )}
        </div>

        <div className={styles.buttons}>
          <StatusActionButton
            variant="secondary"
            onClick={handleCancel}
            size="small"
            disabled={isUploading}
          >
            Отмена
          </StatusActionButton>
          <StatusActionButton
            variant="primary"
            onClick={handleSubmit}
            disabled={(!content.trim() && selectedImages.length === 0) || isUploading || isReadingFiles}
            size="small"
            loading={isUploading}
          >
            {isUploading ? 'Публикация...' : isReadingFiles ? 'Обработка фото...' : 'Опубликовать'}
          </StatusActionButton>
        </div>
      </div>

      {imagePreviews.length > 0 && (
        <div className={styles.imagePreviews}>
          {imagePreviews.map((preview, index) => {
            // Показываем индикатор загрузки, если превью пустое и мы еще читаем файлы
            const isLoading = preview === '' && isReadingFiles;
            // Мгновенно скрываем удаляемые элементы
            const isRemoving = removingIndices.has(index);
            
            if (isRemoving) {
              return null; // Мгновенно скрываем из DOM
            }
            
            return (
              <div key={`preview-${index}-${selectedImages[index]?.name || index}`} className={styles.imagePreview}>
                {isLoading ? (
                  <div className={styles.imagePreviewLoading}>
                    <i className="ri-loader-4-line ri-spin"></i>
                  </div>
                ) : preview ? (
                  <>
                    <img src={preview} alt={`Preview ${index + 1}`} loading="lazy" />
                    <button
                      className={styles.removeImageButton}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveImage(index);
                      }}
                      title="Удалить изображение"
                      disabled={isUploading || isReadingFiles}
                      type="button"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      
      {isReadingFiles && (
        <div className={styles.readingIndicator}>
          <i className="ri-loader-4-line ri-spin"></i>
          <span>
            Чтение фото... {readingProgress.current > 0 && `${readingProgress.current} из ${readingProgress.total}`}
          </span>
        </div>
      )}

      {/* Показываем выбранный объект */}
      {selectedSiteId && (
        <div className={styles.selectedSite}>
          <i className="ri-building-line"></i>
          <span>{getSelectedSiteName()}</span>
          <button 
            className={styles.removeSiteButton}
            onClick={() => setSelectedSiteId(null)}
            title="Убрать привязку к объекту"
            disabled={isUploading}
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      <div className={styles.hint}>
        <i className="ri-information-line"></i>
        Нажмите Ctrl+Enter для быстрой публикации. Используйте #хештеги для тегов.
      </div>

      {/* BottomSheet для выбора объекта */}
      <BottomSheet
        isOpen={showSiteSelector}
        onClose={closeSiteSelector}
        title="Привязать к объекту"
        size="large"
      >
        <div className={styles.siteSelectorSheet}>
          <div className={styles.siteSearchWrap}>
            <i className="ri-search-line" aria-hidden />
            <input
              type="search"
              className={styles.siteSearchInput}
              placeholder="Поиск объекта..."
              value={siteSearchQuery}
              onChange={(e) => setSiteSearchQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
          <ButtonList items={siteSelectorItems} />
        </div>
      </BottomSheet>
    </div>
  );
};

export default CreatePost;

